import { isEmpty, isEmail } from '@jjj/components';
import _capitalize from 'lodash/capitalize';
import actions from './actions';
import { createReducer } from '../../../helpers/reducer';
import { keyBy } from '../../../helpers/core';
import { t } from '../../../i18n';
import { isPostalCode } from '../../../helpers/countriesHelpers';

const initialEditData = {
  name: '',
  address: '',
  street: '',
  city: '',
  state: '',
  zip: '',
  legal_entity_id: '',
  first_name: '',
  last_name: '',
  email: '',
  states_ids: [],
  districts: [],
  markup_percentage: null,
  outside_area_district_id: '',
};

const initState = {
  originalAgencyName: '',
  editForm: {},
  savedAgency: {},
  errors: null,
  formInfo: {
    sectors: [],
    states: [],
  },
  step: 1,
  loading: false,
  isLoadAgentsLoading: false,
  isNewAgencyCreation: true,
  agentsList: undefined,
  editedPositionsMarkup: [],
  agencyLegalEntities: [],
  isLoadAgencyLegalEntitiesLoading: false,
};

function updatePricing(districts, value, districtId, positionId) {
  const markup = value ? value / 100 + 1 : 1;

  districts.forEach(district => {
    const districtMarkup = district.markup_percentage;

    if (districtId === district.id) {
      district.positions.forEach(position => {
        const positionMarkup = position.markup_percentage;

        if ((positionId && position.id === positionId) || (!positionId && districtMarkup === positionMarkup)) {
          position.rates.forEach(rate => {
            rate.bill = (parseFloat(rate.pay) * markup).toFixed(2);
          });
        }
      });
    }
  });

  return districts;
}
function validate(attr, value, isNewAgencyCreation) {
  let error = '';

  if (attr === 'name' && isEmpty(value)) {
    error = t('dash.validation.required_error', { fieldName: t('dash.agencies.invite.name_label') });
  }

  if (attr === 'first_name' && isEmpty(value)) {
    error = t('dash.validation.required_error', { fieldName: t('dash.agencies.invite.first_name_label') });
  }

  if (attr === 'last_name' && isEmpty(value)) {
    error = t('dash.validation.required_error', { fieldName: t('dash.agencies.invite.last_name_label') });
  }

  if (attr === 'email') {
    if (isEmpty(value)) {
      error = t('dash.validation.required_error', { fieldName: t('dash.agencies.invite.email_label') });
    } else if (isNewAgencyCreation && !isEmail(value)) {
      error = t('dash.validation.valid_error', { fieldName: t('dash.agencies.invite.email_label') });
    }
  }

  if (attr === 'states_ids' && isEmpty(value)) {
    error = t('dash.validation.required_error', { fieldName: t('dash.agencies.invite.states_label') });
  }

  if (attr === 'districts' && isEmpty(value)) {
    error = t('dash.validation.required_error', { fieldName: t('dash.agencies.invite.districts_label') });
  }

  if (attr === 'zip') {
    if (!isPostalCode(value)) {
      error = t('dash.validation.valid_error', {
        fieldName: t('dash.agencies.invite.zip_label'),
      });
    }
  }

  return error;
}

function onLoadRequest(state) {
  return { ...state, loading: true };
}

function onLoadSuccess(state, { payload }) {
  const { data, formInfo: formInfoData } = payload;
  const form = data || initialEditData;

  const formInfo = formInfoData || initState.formInfo;
  formInfo.states = formInfo.states
    .filter(item => !!item.districts.length)
    .map(item => {
      item.districts.forEach(district =>
        district.positions.forEach(position => {
          position.enabled = true;
        }),
      );

      return item;
    });

  const propsArray = Object.keys(form);
  const editForm = keyBy(
    propsArray,
    prop => prop,
    prop =>
      prop === 'districts'
        ? form[prop]
        : {
            name: prop,
            value: form[prop],
            error: validate(prop, form[prop]),
            touched: false,
          },
  );

  return {
    ...state,
    editForm,
    originalAgencyName: editForm.name.value,
    formInfo,
    loading: false,
  };
}

function onTouch(state) {
  const editForm = keyBy(
    Object.entries(state.editForm),
    entry => entry[0],
    entry => (entry[0] === 'districts' ? entry[1] : { ...entry[1], touched: true }),
  );

  return { ...state, editForm };
}

function onChangeProperty(state, { payload }) {
  const { attr, value } = payload;
  const { editForm, isNewAgencyCreation } = state;

  return {
    ...state,
    editForm: {
      ...editForm,
      [attr]:
        attr === 'districts'
          ? value
          : {
              value,
              error: validate(attr, value, isNewAgencyCreation),
              name: attr,
              touched: true,
            },
    },
  };
}

function onChangeStep(state, { payload }) {
  return { ...state, step: payload };
}

function onSaveSuccess(state, { payload }) {
  return { ...state, savedAgency: payload };
}

function onSaveError(state, { payload }) {
  const validationErrors = Object.entries(payload.data || {});
  let step = 2;
  const editForm = { ...state.editForm };

  validationErrors.forEach(([attr, error]) => {
    if (editForm[attr]) {
      editForm[attr] =
        attr === 'districts'
          ? editForm[attr]
          : {
              value: editForm[attr].value,
              error: _capitalize(error),
              name: attr,
              touched: true,
            };
    }
  });

  const districtsErrors = {};
  validationErrors.forEach(([attr, error]) => {
    if (error && ['districts'].includes(attr)) {
      step = 2;
      error.forEach(district => {
        const id = district.original_district_id || district.id;
        districtsErrors[id] = district;
        districtsErrors[id].positions = districtsErrors[id].positions.reduce((prev, curr) => {
          prev[curr.position_id] = curr;
          prev[curr.position_id].rates = prev[curr.position_id].rates.reduce((prev_2, curr_2) => {
            prev_2[curr_2.skill_id] = curr_2;

            return prev_2;
          }, {});

          return prev;
        }, {});
      });
    }
  });

  validationErrors.forEach(([attr, error]) => {
    if (error && ['name', 'email'].includes(attr)) {
      step = 1;
    }
  });

  return { ...state, step, editForm, errors: districtsErrors };
}

function onChangeMarkup(state, { payload }) {
  const { markup, district_id, position_id } = payload;

  if (Number.isFinite(markup)) {
    const districts = [...state.editForm.districts];
    updatePricing(districts, markup, district_id, position_id);

    return { ...state, editForm: { ...state.editForm, districts }, errors: null };
  }

  return { ...state };
}

function onChangePositionPricing(state, { payload }) {
  const { district_id, position_id, skill_id, value, attr } = payload;

  const districts = [...state.editForm.districts];
  const district = districts.find(item => item.id === district_id);
  const position = district.positions.find(item => item.id === position_id);
  const rate = position.rates.find(item => item.skill_id === skill_id);

  // Set the correct attribute (pay or bill) with null safety for empty values
  rate[attr] = value === '' || value === null || value === undefined ? null : value;

  const markup = position.markup_percentage;
  const markupMultiplier = markup ? markup / 100 + 1 : 1;

  // Calculate the complementary rate based on which one was edited
  if (attr === 'pay' && rate.pay && !Number.isNaN(parseFloat(rate.pay))) {
    // When pay rate is updated, calculate bill rate
    rate.bill = (parseFloat(rate.pay) * markupMultiplier).toFixed(2);
  } else if (attr === 'bill' && rate.bill && !Number.isNaN(parseFloat(rate.bill))) {
    // When bill rate is updated, calculate pay rate
    rate.pay = (parseFloat(rate.bill) / markupMultiplier).toFixed(2);
  }

  // Keep validation errors persistent - don't auto-clear them
  // Errors will only be cleared when backend validation passes on form submission
  return { ...state, editForm: { ...state.editForm, districts } };
}
function onChangePositionEnabled(state, { payload }) {
  const { district_id, position_id, enabled } = payload;

  const districts = [...state.editForm.districts];
  const district = districts.find(item => item.id === district_id);
  const position = district.positions.find(item => item.id === position_id);
  position.enabled = enabled;
  updatePricing(districts, state.editForm.markup_percentage.value, district_id, position_id);

  return { ...state, editForm: { ...state.editForm, districts } };
}

function setIsNewAgencyCreation(state, { payload }) {
  const { isNewAgencyCreation } = payload;

  return {
    ...state,
    isNewAgencyCreation,
  };
}

function onLoadAgentsListRequest(state) {
  return { ...state, isLoadAgentsLoading: true };
}

function onLoadAgentsListSuccess(state, { payload }) {
  const { agentsList } = payload;

  const { editForm } = state;

  return { ...state, isLoadAgentsLoading: false, editForm, agentsList };
}

function onLoadAgencyLegalEntitiesRequest(state) {
  return { ...state, isLoadAgencyLegalEntitiesLoading: true, agencyLegalEntities: [] };
}

function onLoadAgencyLegalEntitiesSuccess(state, { payload }) {
  return { ...state, isLoadAgencyLegalEntitiesLoading: false, agencyLegalEntities: payload.agencyLegalEntities };
}

function onLoadAgencyLegalEntitiesError(state) {
  return { ...state, isLoadAgencyLegalEntitiesLoading: false };
}

function onClearAgencyLegalEntities(state) {
  return { ...state, agencyLegalEntities: [], isLoadAgencyLegalEntitiesLoading: false };
}

function onAutoFillContactInformation(state, { payload }) {
  const { data } = payload;
  const { isNewAgencyCreation } = state;
  const editForm = { ...state.editForm };

  Object.keys(data).forEach(field => {
    editForm[field] = {
      ...editForm[field],
      value: data[field],
      error: validate(field, data[field], isNewAgencyCreation),
    };
  });

  return { ...state, editForm };
}

function onClearStore() {
  return { ...initState };
}

function onChangeProperties(state, { payload }) {
  const { attributes } = payload;
  const propsArray = Object.keys(attributes);

  const editFormProps = keyBy(
    propsArray,
    prop => prop,
    prop => ({
      name: prop,
      value: attributes[prop],
      error: validate(prop, attributes[prop]),
      touched: true,
    }),
  );

  const editForm = {
    ...state.editForm,
    ...editFormProps,
  };

  return {
    ...state,
    editForm,
  };
}

function onChangeDistrictMarkup(state, { payload }) {
  const { editForm, editedPositionsMarkup } = state;
  const { district_id, markup_percentage } = payload;

  const districts = editForm.districts || [];
  const district = districts.find(item => item.id === district_id);

  if (district) {
    const currentDistrictMarkupPercentage = district.markup_percentage;

    district.markup_percentage = markup_percentage;

    district.positions.forEach(position => {
      if (
        (!position.markup_percentage ||
          parseFloat(position.markup_percentage) === parseFloat(currentDistrictMarkupPercentage)) &&
        !editedPositionsMarkup.includes(`${district_id}-${position.id}`)
      ) {
        position.markup_percentage = markup_percentage;
      }
    });

    return {
      ...state,
      editForm,
    };
  }

  return {
    ...state,
  };
}

function onChangePositionMarkup(state, { payload }) {
  const { editForm, editedPositionsMarkup } = state;
  const { district_id, position_id, markup_percentage } = payload;

  const districts = editForm.districts || [];
  const district = districts.find(item => item.id === district_id);

  if (district) {
    district.positions.forEach(position => {
      if (position.id === position_id) {
        position.markup_percentage = markup_percentage;
      }
    });

    return {
      ...state,
      editForm,
      editedPositionsMarkup: [...editedPositionsMarkup, `${district_id}-${position_id}`],
    };
  }

  return {
    ...state,
  };
}

export default createReducer(initState, {
  [actions.CHANGE_MARKUP]: onChangeMarkup,
  [actions.CHANGE_POSITION_PRICING]: onChangePositionPricing,
  [actions.CHANGE_POSITION_ENABLED]: onChangePositionEnabled,
  [actions.CHANGE_PROPERTY]: onChangeProperty,
  [actions.CHANGE_STEP]: onChangeStep,
  [actions.CLEAR_STORE]: onClearStore,
  [actions.LOAD_REQUEST]: onLoadRequest,
  [actions.LOAD_SUCCESS]: onLoadSuccess,
  [actions.SAVE_ERROR]: onSaveError,
  [actions.SAVE_SUCCESS]: onSaveSuccess,
  [actions.TOUCH]: onTouch,
  [actions.SET_IS_NEW_AGENCY_CREATION]: setIsNewAgencyCreation,
  [actions.LOAD_AGENTS_LIST_REQUEST]: onLoadAgentsListRequest,
  [actions.LOAD_AGENTS_LIST_SUCCESS]: onLoadAgentsListSuccess,
  [actions.LOAD_AGENCY_LEGAL_ENTITIES_REQUEST]: onLoadAgencyLegalEntitiesRequest,
  [actions.LOAD_AGENCY_LEGAL_ENTITIES_SUCCESS]: onLoadAgencyLegalEntitiesSuccess,
  [actions.LOAD_AGENCY_LEGAL_ENTITIES_ERROR]: onLoadAgencyLegalEntitiesError,
  [actions.CLEAR_AGENCY_LEGAL_ENTITIES]: onClearAgencyLegalEntities,
  [actions.AUTOFILL_CONTACT_INFORMATION]: onAutoFillContactInformation,
  [actions.CHANGE_PROPERTIES]: onChangeProperties,
  [actions.CHANGE_DISTRICT_MARKUP]: onChangeDistrictMarkup,
  [actions.CHANGE_POSITION_MARKUP]: onChangePositionMarkup,
});
